import { ApiError } from '../../shared/api/ApiClient.js';

export const CustomerAuthErrorCodes = {
  CREDENTIALS_NOT_CONFIGURED: 'CREDENTIALS_NOT_CONFIGURED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  OTP_DELIVERY_FAILED: 'OTP_DELIVERY_FAILED',
  SMS_NOT_AVAILABLE: 'SMS_NOT_AVAILABLE',
  /** Platform login when the customer has no ACTIVE/PROSPECT membership yet. */
  NO_ACTIVE_MEMBERSHIP: 'NO_ACTIVE_MEMBERSHIP',
  /** Password lockout — API returns HTTP 423 + optional lockedUntil ISO. */
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_ALREADY_CONSUMED: 'TOKEN_ALREADY_CONSUMED',
  TOKEN_VERSION_MISMATCH: 'TOKEN_VERSION_MISMATCH',
  STEP_UP_REQUIRED: 'STEP_UP_REQUIRED',
  LEGAL_CONSENTS_REQUIRED: 'LEGAL_CONSENTS_REQUIRED',
  /** Dual-contact register — email and phone belong to different accounts. */
  CONTACT_IDENTIFIER_CONFLICT: 'CONTACT_IDENTIFIER_CONFLICT',
  /** Callback redirect / API — identity already linked to another IdP subject. */
  OIDC_EMAIL_CONFLICT: 'OIDC_EMAIL_CONFLICT',
  OIDC_EMAIL_CONFLICT_QUERY: 'oidc_email_conflict',
  /** IdP subject already bound to a different customer account. */
  OIDC_SUBJECT_IN_USE: 'OIDC_SUBJECT_IN_USE',
  OIDC_SUBJECT_IN_USE_QUERY: 'oidc_subject_in_use',
  /** Verified IdP email exists without this issuer — Account Link required (no silent merge). */
  OIDC_ACCOUNT_LINK_REQUIRED: 'OIDC_ACCOUNT_LINK_REQUIRED',
  OIDC_ACCOUNT_LINK_REQUIRED_QUERY: 'OIDC_ACCOUNT_LINK_REQUIRED',
  /** SMS / phone verification unavailable under contact policy for OIDC complete. */
  OIDC_PHONE_UNAVAILABLE: 'oidc_phone_unavailable',
} as const;

export type CustomerAuthErrorCode =
  (typeof CustomerAuthErrorCodes)[keyof typeof CustomerAuthErrorCodes];

export function isApiErrorCode(error: Error, code: string): boolean {
  return error instanceof ApiError && error.code === code;
}

export function getApiErrorCode(error: Error): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

/**
 * Additive field on platform NO_ACTIVE_MEMBERSHIP (OTP). Only `false` means
 * offer finish-credentials; omitted/true → membership CTAs only.
 */
export function getApiErrorCredentialsConfigured(error: Error): boolean | undefined {
  if (!(error instanceof ApiError) || typeof error.data !== 'object' || error.data === null) {
    return undefined;
  }
  const raw = (error.data as Record<string, unknown>).credentialsConfigured;
  return typeof raw === 'boolean' ? raw : undefined;
}

/** True when API signals lockout via `ACCOUNT_LOCKED` and/or HTTP 423. */
export function isAccountLockedError(error: Error): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  return (
    error.code === CustomerAuthErrorCodes.ACCOUNT_LOCKED || error.status === 423
  );
}

/** ISO `lockedUntil` from the error envelope when present. */
export function getApiErrorLockedUntil(error: Error): string | undefined {
  if (!(error instanceof ApiError) || typeof error.data !== 'object' || error.data === null) {
    return undefined;
  }
  const raw = (error.data as Record<string, unknown>).lockedUntil;
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}
